import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { ClientDTO } from 'src/app/base/models/models.swagger.generated';
import { ColorPickerFieldComponent } from 'src/app/base/shared/color-picker-field/color-picker-field.component';
import { CollapseButtonComponent } from 'src/app/base/shared/collapse-button/collapse-button.component';
import { TenantThemePayload } from 'src/app/base/services/theme.model';
import { ThemeService } from 'src/app/base/services/theme.service';
import { ThemeTokenOverrideEditorComponent } from './theme-token-override-editor/theme-token-override-editor.component';
import { ThemeQuickEditorComponent } from './theme-quick-editor/theme-quick-editor.component';
import {
  ThemeTokenOverrideRow,
  buildOverridesFromRows,
  buildRowsFromOverrides,
  buildRowsFromSnapshot
} from '../utils/theme-token-override.util';
import {
  TenantThemeSnapshot,
  applyTenantThemePreview,
  buildOverridesFromSnapshot,
  captureTenantThemeSnapshot,
  captureThemeTokenPreviewSnapshot,
  getValidatedHexColor,
  restoreTenantThemeSnapshot
} from '../utils/tenant-theme-snapshot.util';

interface TenantThemePreviewForm {
  primary: FormControl<string | null>;
  secondary: FormControl<string | null>;
  logoUrl: FormControl<string | null>;
  logoUrlDarkMode: FormControl<string | null>;
}

@Component({
  selector: 'app-tenant-theme-editor',
  templateUrl: './tenant-theme-editor.component.html',
  styleUrls: ['./tenant-theme-editor.component.scss'],
  imports: [
    TranslatePipe,
    DialogModule,
    TooltipModule,
    ReactiveFormsModule,
    ColorPickerFieldComponent,
    CollapseButtonComponent,
    ThemeTokenOverrideEditorComponent,
    ThemeQuickEditorComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantThemeEditorComponent implements OnDestroy {
  @ViewChild(ThemeTokenOverrideEditorComponent) public themeTokenOverrideEditor?: ThemeTokenOverrideEditorComponent;

  public themeTokenOverrideRows: ThemeTokenOverrideRow[] = [];
  public isExpanded = true;

  @Input() public isSaving = false;
  @Input() public selectedClient: ClientDTO | null = null;

  @Output() public save = new EventEmitter<TenantThemePayload>();
  @Output() public dirtyChange = new EventEmitter<boolean>();

  public isClientThemeDirty = false;
  private isApplyingClientTheme = false;

  private originalTenantThemeSnapshot: TenantThemeSnapshot;
  private originalThemeTokenPreviewSnapshot = { light: {}, dark: {} };
  private originalLogoUrl = '';
  private originalLogoUrlDarkMode = '';

  public tenantThemePreviewForm = this.formBuilder.group<TenantThemePreviewForm>({
    primary: this.formBuilder.control<string | null>(''),
    secondary: this.formBuilder.control<string | null>(''),
    logoUrl: this.formBuilder.control<string | null>(''),
    logoUrlDarkMode: this.formBuilder.control<string | null>('')
  });

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly themeService: ThemeService,
    private readonly formBuilder: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.originalTenantThemeSnapshot = captureTenantThemeSnapshot(this.document);
    this.captureOriginalLogoUrls();
    this.setTenantThemePreviewFormFromSnapshot(this.originalTenantThemeSnapshot);

    this.tenantThemePreviewForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyTenantThemePreview();
        if (!this.isApplyingClientTheme) {
          this.markDirty(true);
        }
      });
  }

  public ngOnDestroy(): void {
    restoreTenantThemeSnapshot(this.document, this.originalTenantThemeSnapshot);
    this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromSnapshot(this.originalThemeTokenPreviewSnapshot));
  }

  public get hasTenantLogoUrl(): boolean {
    return Boolean(this.resolveTenantLogoPreviewUrl());
  }

  public get tenantLogoPreviewUrl(): string {
    return this.resolveTenantLogoPreviewUrl() || this.themeService.getDefaultLogoUrl();
  }

  public get originalPrimary(): string {
    return this.originalTenantThemeSnapshot.tenantPrimary.trim();
  }

  public get originalSecondary(): string {
    return this.originalTenantThemeSnapshot.tenantSecondary.trim();
  }

  private resolveTenantLogoPreviewUrl(): string {
    const logoUrl = String(this.tenantThemePreviewForm.controls.logoUrl.value ?? '').trim();
    const logoUrlDarkMode = String(this.tenantThemePreviewForm.controls.logoUrlDarkMode.value ?? '').trim();

    return this.themeService.getCurrentMode() === 'dark' && logoUrlDarkMode ? logoUrlDarkMode : logoUrl;
  }

  public toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  public saveTenantThemePreview(): void {
    this.save.emit(this.buildTenantThemePayload());
  }

  public resetTenantThemePreview(): void {
    restoreTenantThemeSnapshot(this.document, this.originalTenantThemeSnapshot);
    this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromSnapshot(this.originalThemeTokenPreviewSnapshot));
    this.setThemeTokenOverrideRowsFromSnapshot(this.originalThemeTokenPreviewSnapshot);
    this.setTenantThemePreviewFormFromSnapshot(this.originalTenantThemeSnapshot);

    if (this.themeTokenOverrideEditor) {
      // Push the reset rows synchronously (ahead of the next change detection pass) so that
      // resetSelection()'s own preview recompute doesn't reapply the stale, pre-reset rows.
      this.themeTokenOverrideEditor.rows = this.themeTokenOverrideRows;
      this.themeTokenOverrideEditor.resetSelection();
    }

    this.markDirty(false);
  }

  public onThemeTokenOverrideRowsChange(rows: ThemeTokenOverrideRow[]): void {
    this.themeTokenOverrideRows = rows;
    this.applyThemeTokenPreviewRows();
    if (!this.isApplyingClientTheme) {
      this.markDirty(true);
    }
  }

  public onTenantThemeColorChanged(controlName: 'primary' | 'secondary', value: string): void {
    this.tenantThemePreviewForm.controls[controlName].setValue(String(value ?? ''), { emitEvent: true });
  }

  public applyLoadedTheme(payload: TenantThemePayload | null): void {
    this.isApplyingClientTheme = true;
    try {
      if (!payload) {
        this.themeService.applyTenantTheme('default', null);
        this.tenantThemePreviewForm.patchValue(
          { primary: '', secondary: '', logoUrl: '', logoUrlDarkMode: '' },
          { emitEvent: true }
        );
        this.themeTokenOverrideRows = [];
        this.applyThemeTokenPreviewRows();
      } else {
        this.tenantThemePreviewForm.patchValue(
          {
            primary: payload.primary ?? '',
            secondary: payload.secondary ?? '',
            logoUrl: payload.logoUrl ?? '',
            logoUrlDarkMode: payload.logoUrlDarkMode ?? ''
          },
          { emitEvent: true }
        );

        this.themeTokenOverrideRows = buildRowsFromOverrides(payload.overrides);
        this.applyThemeTokenPreviewRows();
      }

      this.originalTenantThemeSnapshot = captureTenantThemeSnapshot(this.document);
      this.captureOriginalLogoUrls();
      this.originalThemeTokenPreviewSnapshot = captureThemeTokenPreviewSnapshot(this.document);
      this.setThemeTokenOverrideRowsFromSnapshot(this.originalThemeTokenPreviewSnapshot);
      this.markDirty(false);
    } finally {
      this.isApplyingClientTheme = false;
    }
    this.changeDetector.markForCheck();
  }

  public onSaved(payload: TenantThemePayload | null): void {
    this.applyLoadedTheme(payload);
  }

  private markDirty(isDirty: boolean): void {
    this.isClientThemeDirty = isDirty;
    this.dirtyChange.emit(isDirty);
  }

  private buildTenantThemePayload(): TenantThemePayload {
    const primary = getValidatedHexColor(this.tenantThemePreviewForm.value.primary);
    const secondary = getValidatedHexColor(this.tenantThemePreviewForm.value.secondary);
    const logoUrl = String(this.tenantThemePreviewForm.value.logoUrl ?? '').trim() || undefined;
    const logoUrlDarkMode = String(this.tenantThemePreviewForm.value.logoUrlDarkMode ?? '').trim() || undefined;

    return {
      primary,
      secondary,
      logoUrl,
      logoUrlDarkMode,
      overrides: buildOverridesFromRows(this.themeTokenOverrideRows)
    };
  }

  private applyTenantThemePreview(): void {
    applyTenantThemePreview(this.document, {
      primary: this.tenantThemePreviewForm.value.primary ?? undefined,
      secondary: this.tenantThemePreviewForm.value.secondary ?? undefined,
      logoUrl: this.tenantThemePreviewForm.value.logoUrl ?? undefined,
      logoUrlDarkMode: this.tenantThemePreviewForm.value.logoUrlDarkMode ?? undefined
    }, this.themeService.getCurrentMode());
  }

  private applyThemeTokenPreviewRows(): void {
    this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(this.themeTokenOverrideRows));
  }

  private setThemeTokenOverrideRowsFromSnapshot(snapshot: { light: Record<string, string>; dark: Record<string, string> }): void {
    this.themeTokenOverrideRows = buildRowsFromSnapshot(snapshot);
  }

  private captureOriginalLogoUrls(): void {
    this.originalLogoUrl = this.themeService.getCurrentLogoUrl() ?? '';
    this.originalLogoUrlDarkMode = this.themeService.getCurrentLogoUrlDarkMode() ?? '';
  }

  private setTenantThemePreviewFormFromSnapshot(snapshot: TenantThemeSnapshot): void {
    this.tenantThemePreviewForm.patchValue(
      {
        primary: snapshot.tenantPrimary.trim(),
        secondary: snapshot.tenantSecondary.trim(),
        logoUrl: this.originalLogoUrl,
        logoUrlDarkMode: this.originalLogoUrlDarkMode
      },
      { emitEvent: false }
    );
  }
}
